import React from "react";

import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaQuestionCircle,
  FaTrashAlt,
} from "react-icons/fa";

function sortByOrder(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.ordre || 0) -
      Number(b.ordre || 0)
  );
}

function truncate(
  value = "",
  maxLength = 260
) {
  const text =
    String(value || "");

  if (
    text.length <= maxLength
  ) {
    return text;
  }

  return `${text.slice(
    0,
    maxLength - 1
  )}…`;
}

function getRolesLabel(roles) {
  if (!roles) {
    return "Tous les rôles";
  }

  if (Array.isArray(roles)) {
    return roles.length
      ? roles.join(", ")
      : "Tous les rôles";
  }

  if (typeof roles === "string") {
    try {
      const parsed =
        JSON.parse(roles);

      return Array.isArray(parsed) &&
        parsed.length
        ? parsed.join(", ")
        : "Tous les rôles";
    } catch {
      return roles;
    }
  }

  return "Tous les rôles";
}

export default function TutorialFaqPanel({
  faqs = [],
  busy,
  onCreate,
  onEdit,
  onDelete,
  onToggleActive,
}) {
  const sortedFaqs =
    sortByOrder(faqs);

  return (
    <section>
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-500">
            Centre d’aide
          </div>

          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
            Questions fréquentes
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Ajoute les réponses aux
            questions les plus courantes
            des utilisateurs.
          </p>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={onCreate}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-fuchsia-700 disabled:opacity-50"
        >
          <FaPlus />
          Ajouter une question
        </button>
      </div>

      {sortedFaqs.length ? (
        <div className="space-y-4">
          {sortedFaqs.map(
            (faq, index) => (
              <FaqCard
                key={faq.id}
                faq={faq}
                index={index}
                busy={busy}
                onEdit={() =>
                  onEdit(faq)
                }
                onDelete={() =>
                  onDelete(faq)
                }
                onToggleActive={() =>
                  onToggleActive(faq)
                }
              />
            )
          )}
        </div>
      ) : (
        <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-fuchsia-100 text-2xl text-fuchsia-600 dark:bg-fuchsia-500/15 dark:text-fuchsia-300">
            <FaQuestionCircle />
          </div>

          <h3 className="mt-5 text-xl font-semibold text-slate-900 dark:text-white">
            Aucune question
          </h3>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Ajoute la première question
            fréquente de ce tutoriel.
          </p>

          <button
            type="button"
            disabled={busy}
            onClick={onCreate}
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-fuchsia-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            <FaPlus />
            Ajouter une question
          </button>
        </div>
      )}
    </section>
  );
}

function FaqCard({
  faq,
  index,
  busy,
  onEdit,
  onDelete,
  onToggleActive,
}) {
  const active =
    faq.is_active !== false;

  return (
    <article
      className={`rounded-[24px] border p-5 shadow-sm transition ${
        active
          ? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          : "border-slate-200 bg-slate-100 opacity-65 dark:border-slate-800 dark:bg-slate-900/60"
      }`}
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-fuchsia-100 font-bold text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-300">
          {index + 1}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {faq.question}
            </h3>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                active
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                  : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {active
                ? "Active"
                : "Inactive"}
            </span>
          </div>

          <div className="mt-1 text-xs text-slate-400">
            #{faq.slug} · ordre{" "}
            {faq.ordre}
          </div>

          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {truncate(faq.reponse)}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-medium text-fuchsia-700 dark:bg-fuchsia-500/10 dark:text-fuchsia-300">
              {faq.categorie ||
                "Sans catégorie"}
            </span>

            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {faq.format_reponse ||
                "texte"}
            </span>

            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {getRolesLabel(
                faq.roles_cibles
              )}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-2 lg:justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onToggleActive}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {active ? (
              <FaEyeSlash />
            ) : (
              <FaEye />
            )}

            {active
              ? "Désactiver"
              : "Activer"}
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-sm font-medium text-fuchsia-700 transition hover:bg-fuchsia-100 disabled:opacity-50 dark:border-fuchsia-900/50 dark:bg-fuchsia-500/10 dark:text-fuchsia-300"
          >
            <FaEdit />
            Modifier
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-500/10 dark:text-rose-300"
            title="Supprimer"
          >
            <FaTrashAlt />
          </button>
        </div>
      </div>
    </article>
  );
}